// Minimal track visuals for the grey-box test circuit: asphalt ribbon, white
// edge lines, kerbs and barriers. Real circuits use the full track mesh builder.

import { Color3, Mesh, MeshBuilder, Scene, StandardMaterial, Vector3 } from './babylon';
import type { Track } from '../track/track';

export function buildGreyboxTrack(scene: Scene, track: Track, barrierGap: number): Mesh[] {
  // Offset each centreline point along the right-hand normal of its local direction.
  const edge = (side: 1 | -1, extra: number, y: number): Vector3[] => {
    const n = track.points.length;
    return track.points.map((p, i) => {
      const prev = track.points[(i - 1 + n) % n]!;
      const next = track.points[(i + 1) % n]!;
      const tx = next.x - prev.x;
      const tz = next.z - prev.z;
      const len = Math.hypot(tx, tz) || 1;
      const w = (side > 0 ? p.widthRight : p.widthLeft) + extra;
      return new Vector3(p.x + (tz / len) * w * side, y, p.z + (-tx / len) * w * side);
    });
  };

  const ribbon = (name: string, a: Vector3[], b: Vector3[], color: Color3): Mesh => {
    const m = MeshBuilder.CreateRibbon(name, { pathArray: [a, b], closePath: true, sideOrientation: Mesh.DOUBLESIDE }, scene);
    const mat = new StandardMaterial(name + 'Mat', scene);
    mat.diffuseColor = color;
    mat.specularColor = new Color3(0.08, 0.08, 0.08);
    m.material = mat;
    m.freezeWorldMatrix();
    return m;
  };

  const ground = MeshBuilder.CreateGround('ground', { width: 6000, height: 6000 }, scene);
  const groundMat = new StandardMaterial('groundMat', scene);
  groundMat.diffuseColor = new Color3(0.28, 0.42, 0.2);
  groundMat.specularColor = Color3.Black();
  ground.material = groundMat;
  ground.position.y = -0.05;
  ground.freezeWorldMatrix();

  const meshes: Mesh[] = [
    ground,
    ribbon('road', edge(-1, 0, 0), edge(1, 0, 0), new Color3(0.22, 0.22, 0.24)),
    ribbon('lineL', edge(-1, 0, 0.01), edge(-1, -0.25, 0.01), new Color3(0.95, 0.95, 0.95)),
    ribbon('lineR', edge(1, -0.25, 0.01), edge(1, 0, 0.01), new Color3(0.95, 0.95, 0.95)),
    ribbon('kerbL', edge(-1, 1.2, 0.02), edge(-1, 0, 0.02), new Color3(0.8, 0.12, 0.1)),
    ribbon('kerbR', edge(1, 0, 0.02), edge(1, 1.2, 0.02), new Color3(0.8, 0.12, 0.1)),
  ];

  // Barriers as vertical walls.
  for (const side of [-1, 1] as const) {
    const base = edge(side, barrierGap, 0);
    const top = base.map((v) => new Vector3(v.x, 1.0, v.z));
    meshes.push(ribbon(side > 0 ? 'barrierR' : 'barrierL', base, top, new Color3(0.75, 0.78, 0.82)));
  }

  // Start/finish line across the track at s = 0.
  const f = track.frameAt(0);
  const line = MeshBuilder.CreateGround('startLine', { width: 16, height: 1 }, scene);
  line.position.set(f.x, 0.015, f.z);
  line.rotation.y = f.heading;
  const lineMat = new StandardMaterial('startLineMat', scene);
  lineMat.diffuseColor = new Color3(1, 1, 1);
  line.material = lineMat;
  meshes.push(line);
  return meshes;
}
